import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Heart, MessageCircle, PenSquare, Bookmark, Coins, Trash2, Loader2, AlertTriangle, Send, Rocket, Share2, Check, Copy, QrCode, X } from 'lucide-react';
import CrossPostModal from '../components/CrossPostModal';
import VerifiedBadge from '../components/VerifiedBadge';
import BoostPostModal from '../components/BoostPostModal';
import { nip19 } from 'nostr-tools';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useBookmarks } from '../hooks/useBookmarks';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useBCHAddress } from '../hooks/useBCHAddress';
import { timeAgo } from '../lib/utils';
import BCHTipButton from '../components/BCHTipButton';
import { BCHLogo } from '../components/Layout';
import { INK_NAME, INK_MARK, inkArticleUrl, inkBaseFor } from '../lib/ink';

export default function ArticlePage() {
  const { naddr } = useParams<{ naddr: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const { isBookmarked, toggleBookmark } = useBookmarks();
  const { price: bchPrice } = useBCHPrice();
  const base = inkBaseFor(location.pathname);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCrossPost, setShowCrossPost] = useState(false);
  const [showBoost, setShowBoost] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [sharedToFeed, setSharedToFeed] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [showTipQR, setShowTipQR] = useState(false);
  const [qrAddressCopied, setQrAddressCopied] = useState(false);
  const commentSectionRef = useRef<HTMLDivElement>(null);

  // Published event passed from BlogComposer via router state
  const publishedEvent = (location.state as any)?.publishedEvent || null;

  // Decode naddr
  const decoded = useMemo(() => {
    if (!naddr) return null;
    try {
      const { type, data } = nip19.decode(naddr);
      if (type === 'naddr') return data as { identifier: string; pubkey: string; kind: number; relays?: string[] };
    } catch {}
    return null;
  }, [naddr]);

  // Fetch the article from all relays + local events
  const { events, loading } = useNostrQuery({
    filters: decoded ? [{
      kinds: [30023],
      authors: [decoded.pubkey],
      '#d': [decoded.identifier],
      limit: 5,
    }] : [],
    enabled: !!decoded,
    allRelays: true,
  });

  // Get the latest version — fall back to publishedEvent from router state
  const article = useMemo(() => {
    // Merge relay events with the publishedEvent so we always have it
    const allEvents = publishedEvent ? [...events, publishedEvent] : events;
    if (allEvents.length === 0) return null;
    // Dedupe by id, keep latest
    const seen = new Set<string>();
    const unique = allEvents.filter(e => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });
    return unique.sort((a, b) => b.created_at - a.created_at)[0];
  }, [events, publishedEvent]);

  const { profile } = useNostrProfile(article?.pubkey || decoded?.pubkey);
  const { address: authorBCHAddress } = useBCHAddress(article?.pubkey || decoded?.pubkey);

  // Fetch reactions (likes + comments)
  const articleATag = article && decoded ? `30023:${article.pubkey}:${decoded.identifier}` : '';
  const { events: reactions, refetch: refetchReactions } = useNostrQuery({
    // Fetch likes (7), legacy kind 1 comments, and new NIP-22 kind 1111 comments
    filters: articleATag ? [
      { kinds: [7, 1], '#a': [articleATag], limit: 100 },
      { kinds: [1111], '#A': [articleATag], limit: 100 },
      { kinds: [1111], '#a': [articleATag], limit: 100 },
    ] : [],
    enabled: !!article && !!decoded,
  });

  // Separate comments from likes (both legacy kind 1 and NIP-22 kind 1111)
  const comments = useMemo(() => {
    return reactions
      .filter(e => e.kind === 1 || e.kind === 1111)
      .sort((a, b) => b.created_at - a.created_at);
  }, [reactions]);

  // Fetch tip receipts for this article
  const { events: tipReceipts } = useNostrQuery({
    filters: article ? [{ kinds: [9735], '#e': [article.id], '#t': ['bch-tip'], limit: 200 }] : [],
    enabled: !!article,
  });

  // Calculate total BCH tipped
  const totalTipSats = useMemo(() => {
    let total = 0;
    tipReceipts.forEach(ev => {
      const amountTag = ev.tags.find((t: string[]) => t[0] === 'amount')?.[1];
      if (amountTag) {
        total += parseInt(amountTag, 10) || 0;
      } else {
        try {
          const data = JSON.parse(ev.content);
          total += data.amount_sats || 0;
        } catch {}
      }
    });
    return total;
  }, [tipReceipts]);

  const tipBch = totalTipSats / 1e8;
  const tipUsd = bchPrice ? tipBch * bchPrice : 0;
  const tipCount = tipReceipts.length;

  const likeCount = reactions.filter(e => e.kind === 7).length;
  const commentCount = reactions.filter(e => e.kind === 1 || e.kind === 1111).length;
  const alreadyLiked = user ? reactions.some(e => e.kind === 7 && e.pubkey === user.pubkey) : false;

  if (!decoded) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Invalid article link.</p>
        <Link to={base} className="text-primary text-sm hover:underline mt-2 inline-block">Back to {INK_NAME}</Link>
      </div>
    );
  }

  if (loading && !article) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <BCHLogo size={40} />
        <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">Loading article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Article not found.</p>
        <Link to={base} className="text-primary text-sm hover:underline mt-2 inline-block">Back to {INK_NAME}</Link>
      </div>
    );
  }

  const title = article.tags.find((t: string[]) => t[0] === 'title')?.[1] || 'Untitled';
  const image = article.tags.find((t: string[]) => t[0] === 'image')?.[1];
  const publishedAt = article.tags.find((t: string[]) => t[0] === 'published_at')?.[1];
  const tags = article.tags.filter((t: string[]) => t[0] === 't').map((t: string[]) => t[1]);
  const dTag = article.tags.find((t: string[]) => t[0] === 'd')?.[1] || '';
  const displayName = profile?.display_name || profile?.name || article.pubkey.slice(0, 12) + '...';
  const wordCount = article.content.split(/\s+/).length;
  const readingTime = Math.max(1, Math.round(wordCount / 200));
  const isOwnArticle = user?.pubkey === article.pubkey;
  const bookmarked = isBookmarked(article.id);

  const handleLike = async () => {
    if (!user || alreadyLiked) return;
    await publish({
      kind: 7,
      content: '+',
      tags: [
        ['a', `30023:${article.pubkey}:${dTag}`],
        ['p', article.pubkey],
        ['k', '30023'],
      ],
      created_at: Math.floor(Date.now() / 1000),
    });
  };

  const articleUrl = inkArticleUrl(naddr || '');

  const copyLinkToClipboard = async (): Promise<boolean> => {
    // Prefer the async Clipboard API; fall back to execCommand for older /
    // non-secure contexts where navigator.clipboard is unavailable.
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(articleUrl);
        return true;
      }
    } catch {
      // fall through to legacy path
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = articleUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const flashCopied = () => {
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Share the article to the BCHNostr feed — publishes a short kind 1 note
  // (from the logged-in user) that references the article by its addressable
  // `a` coordinate, so it appears in the feed and links back to the same
  // article. No duplicate content is created.
  const handleShareToFeed = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (sharing || sharedToFeed) return;
    setSharing(true);
    setShareError(null);
    try {
      const summary = article.tags.find((t: string[]) => t[0] === 'summary')?.[1]
        || article.content.slice(0, 160).replace(/[#*_[\]`>]/g, '').trim() + '…';
      await publish({
        kind: 1,
        content: `${INK_MARK} ${isOwnArticle ? 'New on' : 'Reading on'} ${INK_NAME}\n\n"${title}"\nby ${displayName}\n\n${summary}\n\nRead it \u2192 ${articleUrl}\n\n\uD83D\uDC9A Tip \u00B7 \uD83D\uDCAC Discuss\n\n#bch #bitcoincash #bchink`,
        tags: [
          ['t', 'bch'], ['t', 'bitcoincash'], ['t', 'bchink'],
          // Reference the article by coordinate (mention) + author + link.
          ['a', `30023:${article.pubkey}:${dTag}`, '', 'mention'],
          ['p', article.pubkey],
          ['r', articleUrl],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setSharedToFeed(true);
      setTimeout(() => setSharedToFeed(false), 4000);
    } catch (err) {
      console.error('Share to feed failed:', err);
      setShareError('Could not share. Please try again.');
    } finally {
      setSharing(false);
    }
  };

  const handleCopyLink = async () => {
    await copyLinkToClipboard();
    flashCopied();
  };

  const handleDelete = async () => {
    if (!user || !article || !isOwnArticle) return;
    setDeleting(true);
    try {
      await publish({
        kind: 5,
        content: '',
        tags: [
          ['e', article.id],
          ['a', `30023:${article.pubkey}:${dTag}`],
          ['k', '30023'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      // Remove from local events cache so it disappears immediately
      try {
        const key = 'bchnostr:local-events';
        const raw = localStorage.getItem(key);
        if (raw) {
          const local = JSON.parse(raw) as any[];
          const filtered = local.filter(e => e.id !== article.id);
          localStorage.setItem(key, JSON.stringify(filtered));
        }
      } catch {}

      navigate(base, { replace: true });
    } catch (err) {
      console.error('Delete failed:', err);
    }
    setDeleting(false);
    setShowDeleteConfirm(false);
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back nav */}
      <div className="px-4 py-3 border-b border-border">
        <Link to={base} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to {INK_NAME}
        </Link>
      </div>

      {/* Cover image */}
      {image && (
        <div className="aspect-[2.5/1] overflow-hidden bg-muted">
          <img src={image} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      <article className="px-4 sm:px-6 py-6">
        {/* Title */}
        <h1 className="text-2xl sm:text-3xl font-bold leading-tight mb-4">{title}</h1>

        {/* Author + meta */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
          <Link to={`/profile/${article.pubkey}`} className="flex items-center gap-3">
            {profile?.picture ? (
              <img src={profile.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-5 h-5 text-primary/60" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-1">
                <p className="font-semibold text-sm">{displayName}</p>
                <VerifiedBadge nip05={profile?.nip05} pubkey={article.pubkey} size="sm" />
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{timeAgo(publishedAt ? parseInt(publishedAt) : article.created_at)}</span>
                <span>·</span>
                <span>{readingTime} min read</span>
                <span>·</span>
                <span>{wordCount.toLocaleString()} words</span>
              </div>
            </div>
          </Link>
          {isOwnArticle && (
            <div className="flex items-center gap-1.5">
              <Link
                to={`${base}/write?edit=${naddr}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted"
              >
                <PenSquare className="w-3.5 h-3.5" /> Edit
              </Link>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-500/20 text-xs font-medium text-red-500 hover:bg-red-500/5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          )}
        </div>

        {/* Tips earned banner */}
        {totalTipSats > 0 && (
          <div className="flex items-center gap-2 mb-6 px-4 py-3 rounded-xl bg-emerald-500/5 border border-emerald-500/15">
            <Coins className="w-5 h-5 text-emerald-500 shrink-0" />
            <div className="flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                  {tipUsd >= 0.01 ? `$${tipUsd.toFixed(2)}` : `${totalTipSats.toLocaleString()} sats`}
                </span>
                <span className="text-xs text-muted-foreground">
                  earned from {tipCount} tip{tipCount !== 1 ? 's' : ''}
                </span>
              </div>
              {tipUsd >= 0.01 && (
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {tipBch.toFixed(tipBch < 0.001 ? 6 : 4)} BCH · {totalTipSats.toLocaleString()} sats
                </p>
              )}
            </div>
          </div>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-6">
            {tags.map((tag: string) => (
              <Link key={tag} to={`/search?q=%23${tag}`} className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20">
                #{tag}
              </Link>
            ))}
          </div>
        )}

        {/* Markdown body */}
        <div className="prose-bch">
          <MarkdownRenderer content={article.content} />
        </div>

        {/* ── Primary actions: Tip · Boost · Discuss · Share ── */}
        <div className="mt-8 pt-5 border-t border-border space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {/* 💚 Tip — existing BCH tipping system */}
            <div className="[&>*]:w-full [&_button]:w-full [&_button]:justify-center [&_button]:px-3 [&_button]:py-2.5 [&_button]:rounded-xl [&_button]:border [&_button]:border-emerald-500/25 [&_button]:bg-emerald-500/5 [&_button]:text-emerald-600 dark:[&_button]:text-emerald-400 [&_button]:font-semibold [&_button]:text-sm">
              <BCHTipButton recipientPubkey={article.pubkey} recipientProfile={profile} eventId={article.id} />
            </div>

            {/* 🚀 Boost — existing Boost system */}
            <button
              onClick={() => setShowBoost(true)}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-primary/25 bg-primary/5 text-primary font-semibold text-sm hover:bg-primary/10 transition-colors"
            >
              <Rocket className="w-4 h-4" /> Boost
            </button>

            {/* 💬 Discuss — scroll to Nostr discussion */}
            <button
              onClick={() => commentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-border bg-card text-foreground font-semibold text-sm hover:bg-muted transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              Discuss{commentCount > 0 ? ` · ${commentCount}` : ''}
            </button>

            {/* ↗ Share — post the article to the BCHNostr feed */}
            <button
              onClick={handleShareToFeed}
              disabled={sharing || sharedToFeed}
              title="Share this article to the BCHNostr feed"
              className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border font-semibold text-sm transition-colors disabled:opacity-70 ${
                sharedToFeed
                  ? 'border-emerald-500/25 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400'
                  : 'border-border bg-card text-foreground hover:bg-muted'
              }`}
            >
              {sharing
                ? <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                : sharedToFeed ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {sharing ? 'Sharing…' : sharedToFeed ? 'Shared!' : 'Share'}
            </button>
          </div>

          {shareError && (
            <p className="text-xs text-red-500">{shareError}</p>
          )}

          {/* Secondary, low-emphasis actions */}
          <div className="flex items-center gap-1 flex-wrap text-muted-foreground">
            <button
              onClick={handleLike}
              disabled={!user || alreadyLiked}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                alreadyLiked ? 'text-red-500 bg-red-500/5' : 'hover:text-red-500 hover:bg-red-500/5'
              }`}
            >
              <Heart className={`w-4 h-4 ${alreadyLiked ? 'fill-current' : ''}`} />
              {likeCount > 0 ? likeCount : 'Like'}
            </button>

            <button
              onClick={async () => { await toggleBookmark(article.id); }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                bookmarked ? 'text-primary bg-primary/5' : 'hover:text-primary hover:bg-primary/5'
              }`}
            >
              <Bookmark className={`w-4 h-4 ${bookmarked ? 'fill-current' : ''}`} />
              {bookmarked ? 'Saved' : 'Save'}
            </button>

            {authorBCHAddress && !isOwnArticle && (
              <button
                onClick={() => setShowTipQR(true)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs hover:text-emerald-600 hover:bg-emerald-500/5 transition-colors"
                title="Pay with an external wallet via QR code"
              >
                <QrCode className="w-4 h-4" /> QR Pay
              </button>
            )}

            <button
              onClick={() => setShowCrossPost(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs hover:text-foreground hover:bg-muted transition-colors"
            >
              <Share2 className="w-4 h-4" /> Cross-post
            </button>

            <button
              onClick={handleCopyLink}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                linkCopied ? 'text-emerald-600 dark:text-emerald-400' : 'hover:text-foreground hover:bg-muted'
              }`}
            >
              {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {linkCopied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        </div>

        {/* Cross-post modal */}
        {showCrossPost && (
          <CrossPostModal
            content={`"${title}" — ${article.tags.find((t: string[]) => t[0] === 'summary')?.[1] || article.content.slice(0, 200).replace(/[#*_\[\]]/g, '')}`}
            postUrl={articleUrl}
            onClose={() => setShowCrossPost(false)}
          />
        )}

        {/* Boost modal — existing Boost system, keyed to the article event */}
        {showBoost && (
          <BoostPostModal
            eventId={article.id}
            onClose={() => setShowBoost(false)}
          />
        )}
      </article>

      {/* BCH QR Payment Modal */}
      {showTipQR && authorBCHAddress && (
        <BCHQRPayModal
          address={authorBCHAddress}
          authorName={displayName}
          authorAvatar={profile?.picture}
          articleTitle={title}
          onClose={() => { setShowTipQR(false); setQrAddressCopied(false); }}
          copied={qrAddressCopied}
          onCopy={() => {
            navigator.clipboard.writeText(authorBCHAddress).catch(() => {});
            setQrAddressCopied(true);
            setTimeout(() => setQrAddressCopied(false), 2000);
          }}
        />
      )}

      {/* Comments Section */}
      <div ref={commentSectionRef} className="max-w-3xl mx-auto px-4 sm:px-6 pb-10">
        <div className="border-t border-border pt-6">
          <h2 className="text-base font-bold flex items-center gap-2 mb-4">
            <MessageCircle className="w-4 h-4 text-primary" />
            {commentCount > 0 ? `Discussion · ${commentCount}` : 'Discussion'}
          </h2>

          {/* Comment composer */}
          <ArticleCommentComposer
            article={article}
            decoded={decoded}
            dTag={dTag}
            onPublished={() => { setTimeout(() => refetchReactions(), 1000); }}
          />

          {/* Comments list */}
          {comments.length > 0 ? (
            <div className="mt-4 space-y-0">
              {comments.map(comment => (
                <CommentCard
                  key={comment.id}
                  event={comment}
                  articlePubkey={article.pubkey}
                  article={article}
                  decoded={decoded}
                  dTag={dTag}
                  onReplyPublished={() => { setTimeout(() => refetchReactions(), 800); }}
                />
              ))}
            </div>
          ) : (
            <div className="mt-6 text-center py-8">
              <MessageCircle className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No comments yet. Be the first!</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Delete Article</h3>
                <p className="text-xs text-muted-foreground mt-0.5">This cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-1">
              Are you sure you want to delete <strong className="text-foreground">"{title}"</strong>?
            </p>
            <p className="text-xs text-muted-foreground mb-5">
              A deletion request will be sent to all relays. Most relays will remove it, but some may still keep a copy.
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-border hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Article Comment Composer ──────────────────────────────────────

function ArticleCommentComposer({ article, decoded, dTag, onPublished }: {
  article: any;
  decoded: any;
  dTag: string;
  onPublished: () => void;
}) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const [content, setContent] = useState('');
  const [publishing, setPublishing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handlePublish = async () => {
    if (!user || !content.trim()) return;
    setPublishing(true);
    try {
      const aTag = `30023:${article.pubkey}:${dTag}`;
      await publish({
        kind: 1111,
        content: content.trim(),
        tags: [
          // NIP-22: root scope (uppercase)
          ['A', aTag, '', article.pubkey],
          ['K', '30023'],
          ['P', article.pubkey],
          // NIP-22: parent item (same as root for top-level comments)
          ['a', aTag, '', article.pubkey],
          ['e', article.id, ''],
          ['k', '30023'],
          ['p', article.pubkey],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setContent('');
      onPublished();
    } catch (e) {
      console.error('Comment publish failed:', e);
    } finally {
      setPublishing(false);
    }
  };

  if (!user) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-muted-foreground mb-2">Log in to join the conversation</p>
        <button
          onClick={() => setShowLoginDialog(true)}
          className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90"
        >
          Log In
        </button>
      </div>
    );
  }

  return (
    <div className="flex gap-3 py-3">
      {user.picture ? (
        <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0 mt-0.5" />
      ) : (
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
          <User className="w-4 h-4 text-primary/60" />
        </div>
      )}
      <div className="flex-1 min-w-0 rounded-2xl border border-border bg-muted/30 px-4 py-2.5">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Write a comment…"
          className="w-full bg-transparent resize-none text-sm placeholder:text-muted-foreground focus:outline-none min-h-[44px] leading-relaxed"
          rows={2}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handlePublish();
            }
          }}
        />
        {content.trim() && (
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/40 mt-1.5">
            <button onClick={() => setContent('')} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Cancel
            </button>
            <button
              onClick={handlePublish}
              disabled={publishing}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-95"
            >
              {publishing
                ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <Send className="w-3 h-3" />}
              Post
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Comment Card ─────────────────────────────────────────────────

function CommentCard({ event, articlePubkey, article, decoded, dTag, onReplyPublished }: {
  event: any;
  articlePubkey: string;
  article: any;
  decoded: any;
  dTag: string;
  onReplyPublished: () => void;
}) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile } = useNostrProfile(event.pubkey);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [replying, setReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [publishingReply, setPublishingReply] = useState(false);
  const replyRef = useRef<HTMLTextAreaElement>(null);

  const isAuthor = event.pubkey === articlePubkey;
  const displayName = profile?.display_name || profile?.name || event.pubkey.slice(0, 8) + '…';

  useEffect(() => {
    if (replying && replyRef.current) replyRef.current.focus();
  }, [replying]);

  const handleLike = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (liked) return;
    setLiked(true);
    setLikeCount(c => c + 1);
    await publish({
      kind: 7, content: '+',
      tags: [['e', event.id], ['p', event.pubkey]],
      created_at: Math.floor(Date.now() / 1000),
    });
  };

  const handleReply = async () => {
    if (!user || !replyContent.trim()) return;
    setPublishingReply(true);
    try {
      const aTag = `30023:${article.pubkey}:${dTag}`;
      await publish({
        kind: 1111,
        content: replyContent.trim(),
        tags: [
          // NIP-22: root scope — the article (uppercase)
          ['A', aTag, '', article.pubkey],
          ['K', '30023'],
          ['P', article.pubkey],
          // NIP-22: parent item — the comment we're replying to (lowercase)
          ['e', event.id, '', event.pubkey],
          ['k', event.kind === 1111 ? '1111' : '1'],
          ['p', event.pubkey],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setReplyContent('');
      setReplying(false);
      onReplyPublished();
    } catch (e) {
      console.error('Reply failed:', e);
    } finally {
      setPublishingReply(false);
    }
  };

  return (
    <div className="flex gap-3 py-3 border-b border-border/60 last:border-b-0">
      {/* Avatar */}
      <Link to={`/profile/${event.pubkey}`} className="shrink-0 mt-0.5">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover hover:opacity-80 transition-opacity" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-4 h-4 text-primary/60" />
          </div>
        )}
      </Link>

      {/* Body */}
      <div className="flex-1 min-w-0">
        {/* Bubble */}
        <div className="bg-muted/50 rounded-2xl rounded-tl-sm px-3.5 py-2.5 inline-block max-w-full">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <Link to={`/profile/${event.pubkey}`} className="font-semibold text-sm hover:underline leading-none">
              {displayName}
            </Link>
            <VerifiedBadge nip05={profile?.nip05} pubkey={event.pubkey} size="xs" />
            {isAuthor && (
              <span className="text-[9px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide">Author</span>
            )}
          </div>
          <p className="text-sm text-foreground whitespace-pre-wrap break-words leading-relaxed">
            {event.content}
          </p>
        </div>

        {/* Meta + actions below bubble */}
        <div className="flex items-center gap-3 mt-1 ml-1">
          <span className="text-[11px] text-muted-foreground">{timeAgo(event.created_at)}</span>

          <button
            onClick={handleLike}
            className={`flex items-center gap-1 text-[11px] font-medium transition-colors ${
              liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
            }`}
          >
            <Heart className={`w-3 h-3 ${liked ? 'fill-current' : ''}`} />
            {likeCount > 0 ? likeCount : 'Like'}
          </button>

          <button
            onClick={() => { if (!user) { setShowLoginDialog(true); return; } setReplying(r => !r); }}
            className={`text-[11px] font-medium transition-colors ${
              replying ? 'text-primary' : 'text-muted-foreground hover:text-primary'
            }`}
          >
            Reply
          </button>

          <BCHTipButton recipientPubkey={event.pubkey} recipientProfile={profile} eventId={event.id} />
        </div>

        {/* Inline reply composer */}
        {replying && (
          <div className="mt-2 flex gap-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <User className="w-3.5 h-3.5 text-primary/60" />
              </div>
            )}
            <div className="flex-1 min-w-0 rounded-2xl rounded-tl-sm border border-border bg-muted/30 px-3 py-2">
              <textarea
                ref={replyRef}
                value={replyContent}
                onChange={e => setReplyContent(e.target.value)}
                placeholder={`Reply to ${displayName}…`}
                rows={2}
                className="w-full bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none resize-none leading-relaxed"
                onKeyDown={e => {
                  if (e.key === 'Escape') { setReplying(false); setReplyContent(''); }
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleReply(); }
                }}
              />
              {replyContent.trim() && (
                <div className="flex items-center justify-end gap-2 pt-1.5 border-t border-border/40 mt-1">
                  <button onClick={() => { setReplying(false); setReplyContent(''); }}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={handleReply}
                    disabled={publishingReply}
                    className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-40 transition-all active:scale-95"
                  >
                    {publishingReply
                      ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <Send className="w-3 h-3" />}
                    Reply
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── BCH QR Pay Modal ─────────────────────────────────────────────

interface BCHQRPayModalProps {
  address: string;
  authorName: string;
  authorAvatar?: string;
  articleTitle: string;
  onClose: () => void;
  copied: boolean;
  onCopy: () => void;
}

function BCHQRPayModal({ address, authorName, authorAvatar, articleTitle, onClose, copied, onCopy }: BCHQRPayModalProps) {
  const displayAddress = address.replace(/^bitcoincash:/i, '');
  const qrValue = address; // full URI with prefix for wallet compatibility
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=12&format=png&data=${encodeURIComponent(qrValue)}`;
  const [qrLoaded, setQrLoaded] = useState(false);
  const [qrError, setQrError] = useState(false);

  // Prevent background scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:w-auto sm:min-w-[340px] sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-card border border-border shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center">
              <QrCode className="w-4 h-4 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm font-bold">Pay with BCH</p>
              <p className="text-[11px] text-muted-foreground">Scan with any BCH wallet</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Author info */}
        <div className="flex items-center gap-2.5 px-5 pb-3">
          {authorAvatar ? (
            <img src={authorAvatar} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-3.5 h-3.5 text-primary/60" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-xs font-medium truncate">Tip <span className="text-primary">{authorName}</span></p>
            <p className="text-[10px] text-muted-foreground truncate">for "{articleTitle}"</p>
          </div>
        </div>

        {/* QR Code */}
        <div className="flex flex-col items-center px-5 pb-4 gap-3">
          <div className="w-[200px] h-[200px] rounded-xl border-2 border-border bg-white flex items-center justify-center overflow-hidden relative">
            {!qrLoaded && !qrError && (
              <div className="absolute inset-0 flex items-center justify-center bg-white">
                <div className="w-7 h-7 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
              </div>
            )}
            {qrError ? (
              <div className="flex flex-col items-center gap-2 p-4 text-center">
                <QrCode className="w-8 h-8 text-muted-foreground/40" />
                <p className="text-[10px] text-muted-foreground">QR unavailable — copy address below</p>
              </div>
            ) : (
              <img
                src={qrUrl}
                alt="BCH payment QR code"
                className={`w-full h-full object-contain transition-opacity duration-300 ${qrLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setQrLoaded(true)}
                onError={() => { setQrError(true); setQrLoaded(true); }}
                loading="eager"
              />
            )}
          </div>

          {/* BCH address — truncated display, full address on copy */}
          <div className="w-full rounded-xl border border-border bg-muted/40 px-3 py-2.5">
            <p className="text-[10px] text-muted-foreground mb-1 font-medium uppercase tracking-wide">BCH Address</p>
            <p className="text-xs font-mono text-foreground break-all leading-relaxed select-all">{displayAddress}</p>
          </div>

          {/* Copy button */}
          <button
            onClick={onCopy}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
              copied
                ? 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/30'
                : 'bg-emerald-500 text-white hover:bg-emerald-600 active:scale-[0.98]'
            }`}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                Address Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy Address
              </>
            )}
          </button>

          {/* Hint */}
          <p className="text-[10px] text-muted-foreground text-center leading-relaxed px-2 pb-1">
            Open your Bitcoin Cash wallet, tap "Send", and scan this code — or paste the address above.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Simple Markdown Renderer ─────────────────────────────────────
// Renders a subset of markdown without pulling in a heavy library.
// Supports: headings, bold, italic, links, images, code blocks,
// inline code, lists, blockquotes, horizontal rules.

function MarkdownRenderer({ content }: { content: string }) {
  const html = useMemo(() => renderMarkdown(content), [content]);
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

function renderMarkdown(md: string): string {
  let html = escapeHtml(md);

  // Code blocks (```...```)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, lang, code) =>
    `<pre class="bg-muted rounded-lg p-4 overflow-x-auto text-sm font-mono my-4"><code>${code.trim()}</code></pre>`
  );

  // Inline code
  html = html.replace(/`([^`\n]+)`/g, '<code class="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="max-w-full rounded-lg my-4" loading="lazy" />');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>');

  // Headings (must be done line-by-line)
  html = html.replace(/^#### (.+)$/gm, '<h4 class="text-base font-bold mt-6 mb-2">$1</h4>');
  html = html.replace(/^### (.+)$/gm, '<h3 class="text-lg font-bold mt-6 mb-2">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold mt-8 mb-3">$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-8 mb-3">$1</h1>');

  // Horizontal rule
  html = html.replace(/^---+$/gm, '<hr class="my-6 border-border" />');

  // Blockquotes
  html = html.replace(/^&gt; (.+)$/gm, '<blockquote class="border-l-4 border-primary/30 pl-4 py-1 my-3 text-muted-foreground italic">$1</blockquote>');

  // Bold + italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong class="font-bold"><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold">$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Unordered lists
  html = html.replace(/^[\-\*] (.+)$/gm, '<li class="ml-4 list-disc">$1</li>');
  // Wrap consecutive <li> tags
  html = html.replace(/((?:<li[^>]*>.*<\/li>\n?)+)/g, '<ul class="my-3 space-y-1">$1</ul>');

  // Ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>');
  html = html.replace(/((?:<li class="ml-4 list-decimal">.*<\/li>\n?)+)/g, '<ol class="my-3 space-y-1">$1</ol>');

  // Paragraphs — wrap remaining lines that aren't already wrapped in tags
  html = html.replace(/^(?!<[a-z])((?!<).+)$/gm, '<p class="my-3 leading-relaxed">$1</p>');

  // Clean up empty paragraphs
  html = html.replace(/<p class="my-3 leading-relaxed"><\/p>/g, '');
  // Clean double wrapping
  html = html.replace(/<p class="my-3 leading-relaxed">(<(?:h[1-4]|ul|ol|blockquote|pre|hr|img)[^>]*>)/g, '$1');

  // Auto-link bare URLs
  html = html.replace(/(https?:\/\/[^\s<"]+)/g, (url) => {
    // Don't double-link URLs already inside <a> or <img> tags
    return url;
  });

  return html;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

